import { Component, computed, inject, input, signal, viewChild } from '@angular/core';
import { UiButtonComponent } from '@design-system';
import { OrderSide } from '@shared-types';
import { IOrderActionButton, IOrderActionButtonType } from '../../../../models/order-form.model';
import { BottomSheetComponent } from '../../../../components/bottom-sheet/bottom-sheet.component';
import { CommodityAccessDeniedBottomSheetComponent } from '../../../../components/commodity-access-denied-bottom-sheet/commodity-access-denied-bottom-sheet.component';
import { CommodityAccessService } from '../../../../services/commodity-access.service';
import { calculateTradeValue } from '../../tools/order-form-summary.tools';
import { OrderConfirmComponent } from '../order-confirm/order-confirm.component';
import { OrderConfirmSummary } from '../order-confirm/order-confirm.model';
import { OrderFormFacade } from '../../data-access/order-form.facade';

@Component({
	selector: 'app-order-form-actions',
	templateUrl: './order-form-actions.component.html',
	imports: [UiButtonComponent, BottomSheetComponent, OrderConfirmComponent, CommodityAccessDeniedBottomSheetComponent]
})
export class OrderFormActionsComponent {
	readonly orderFormFacade = inject(OrderFormFacade);

	private readonly commodityAccess = inject(CommodityAccessService);

	readonly isLoading = input(false);
	/** Shown on the confirm sheet — the page owns the symbol snapshot, not the facade. */
	readonly symbolName = input('');

	readonly orderConfirmSheet = viewChild<BottomSheetComponent>('orderConfirmSheet');

	readonly accessDeniedSheet = viewChild<CommodityAccessDeniedBottomSheetComponent>('accessDeniedSheet');

	readonly actionButtons = computed(() => this.orderFormFacade.actionButtons() ?? []);

	readonly isRequestLoading = computed(() => this.isLoading() || this.orderFormFacade.submitting());

	/** Null until a button opens the sheet, so the summary is never rendered from placeholders. */
	readonly confirmSummary = signal<OrderConfirmSummary | null>(null);

	private pendingAction = signal<IOrderActionButtonType | null>(null);

	onButtonClick(button: IOrderActionButton): void {
		if (button.disabled || this.isRequestLoading()) {
			return;
		}

		// ارسال سفارش is where a customer without a کد بورس کالا is stopped, and it is stopped
		// here — ahead of the confirmation and ahead of the facade — so nothing is captured, no
		// confirm is shown, and no order is built for a request that must not leave.
		if (button.action === 'SUBMIT' && this.commodityAccess.denied()) {
			this.accessDeniedSheet()?.open();
			return;
		}

		if (button.action === 'EDIT' && this.orderFormFacade.notifyIfEditUnchanged()) {
			return;
		}

		if (button.confirmBeforeAction && this.hasOrderValues()) {
			this.openOrderConfirm(button.action);
			return;
		}

		this.orderFormFacade.handleAction(button.action);
	}

	private hasOrderValues(): boolean {
		const { price, quantity } = this.orderFormFacade.getFormValue();
		return !!price || !!quantity;
	}

	/** Dispatches before closing, so the snapshot is still pinned when the payload is built. */
	onConfirm(): void {
		const action = this.pendingAction();
		if (action) {
			this.orderFormFacade.handleAction(action);
		}
		this.closeOrderConfirm();
	}

	onCancelConfirm(): void {
		this.closeOrderConfirm();
	}

	onConfirmClosed(): void {
		this.pendingAction.set(null);
		this.confirmSummary.set(null);
		this.orderFormFacade.clearConfirmSnapshot();
	}

	private openOrderConfirm(action: IOrderActionButtonType): void {
		this.orderFormFacade.captureConfirmSnapshot();
		this.pendingAction.set(action);
		this.confirmSummary.set(this.buildConfirmSummary(action === 'EDIT'));
		this.orderConfirmSheet()?.open();
	}

	private closeOrderConfirm(): void {
		this.pendingAction.set(null);
		this.orderConfirmSheet()?.close();
	}

	/**
	 * The trade value is calculated exactly as the order-form summary does, so the sheet
	 * confirms the same جمع کل the user was reading a moment earlier.
	 */
	private buildConfirmSummary(isEdit: boolean): OrderConfirmSummary {
		const { side, price, quantity } = this.orderFormFacade.getFormValue();
		const enteredPrice = price ?? 0;
		const enteredQuantity = quantity ?? 0;
		const buyRate = this.orderFormFacade.buyCommissionRate();
		const sellRate = this.orderFormFacade.sellCommissionRate();

		const orderConfirmationEnabled = this.orderFormFacade.orderConfirmationEnabled();

		return {
			symbolName: this.symbolName(),
			side,
			sideText: side === OrderSide.Sell ? 'فروش' : 'خرید',
			quantity: enteredQuantity,
			price: enteredPrice,
			totalValue: calculateTradeValue(side, enteredPrice, enteredQuantity, buyRate, sellRate),
			isEdit,
			maxVolume: this.orderFormFacade.validRanges().volume.max,
			orderConfirmationEnabled
		};
	}
}
